import {
  BadgeCheck,
  Box,
  Briefcase,
  CircleDollarSign,
  ClipboardCheck,
  FileStack,
  Landmark,
  Layers,
  ListTodo,
  LucideUserCircle2,
  Mail,
  MessageCircleQuestion,
  ScrollText,
  Users,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useMemo } from 'react';
import { t } from '@lingui/core/macro';
import type { AdminPermissionKey } from '@duke-hq/svc-iam-policy';
import { useTenantContext } from './TenantContext';

export type AdminNavItem = {
  label: string;
  description: string;
  icon: LucideIcon;
  link: string;
  permission?: AdminPermissionKey;
  hasSubSidebar: boolean;
};

export function useAdminNavItems(): AdminNavItem[] {
  const tenantCtx = useTenantContext();
  const permissions = tenantCtx?.permissions;

  return useMemo(() => {
    const perms = permissions ?? [];
    const items: AdminNavItem[] = [
      {
        label: t`Support`,
        description: t`Respond to support enquiries from students and providers.`,
        icon: MessageCircleQuestion,
        link: '/support',
        permission: 'canViewSupportEnquiries',
        hasSubSidebar: true,
      },
      {
        label: t`Support Tasks`,
        description: t`View support tasks and the rules that create them.`,
        icon: ClipboardCheck,
        link: '/tasks',
        permission: 'canViewSupportTasks',
        hasSubSidebar: true,
      },
      {
        label: t`Units`,
        description: t`Manage units of competency and their content.`,
        icon: Layers,
        link: '/units',
        permission: 'canManageCourseContent',
        hasSubSidebar: true,
      },
      {
        label: t`Courses`,
        description: t`Manage courses and their structure.`,
        icon: Box,
        link: '/courses',
        permission: 'canManageCourseContent',
        hasSubSidebar: false,
      },
      {
        label: t`Users`,
        description: t`Manage admin users and their permissions.`,
        icon: Users,
        link: '/users',
        hasSubSidebar: false,
      },
      {
        label: t`Students`,
        description: t`View student records and progress.`,
        icon: LucideUserCircle2,
        link: '/students',
        permission: 'canViewStudents',
        hasSubSidebar: true,
      },
      {
        label: t`Enrolments`,
        description: t`View and manage student enrolments.`,
        icon: ScrollText,
        link: '/enrolments',
        permission: 'canViewEnrolments',
        hasSubSidebar: false,
      },
      {
        label: t`Placements`,
        description: t`Manage student work placements.`,
        icon: Briefcase,
        link: '/placements',
        permission: 'canManageWorkPlacements',
        hasSubSidebar: true,
      },
      {
        label: t`Providers`,
        description: t`Manage employment services providers.`,
        icon: Landmark,
        link: '/providers',
        permission: 'canViewProviders',
        hasSubSidebar: false,
      },
      {
        label: t`Organisation`,
        description: t`Manage this training organisation's identity and branding.`,
        icon: BadgeCheck,
        link: '/organisation',
        permission: 'canManageOrganisationSettings',
        hasSubSidebar: false,
      },
      {
        label: t`Templates`,
        description: t`Read the emails sent on your organisation's behalf.`,
        icon: Mail,
        link: '/templates',
        permission: 'canManageTemplates',
        hasSubSidebar: false,
      },
      {
        label: t`Assessments`,
        description: t`Review and manage student assessments.`,
        icon: ListTodo,
        link: '/assessments',
        permission: 'canViewAssessments',
        hasSubSidebar: true,
      },
      {
        label: t`Finances`,
        description: t`Manage invoices, billing, and finance reporting.`,
        icon: CircleDollarSign,
        link: '/finances/invoices',
        permission: 'canViewFinances',
        hasSubSidebar: true,
      },
      {
        label: t`AVETMISS`,
        description: t`Generate AVETMISS reports for VET regulators.`,
        icon: FileStack,
        link: '/avetmiss',
        permission: 'canViewAvetmiss',
        hasSubSidebar: true,
      },
      {
        label: t`Developer Tools`,
        description: t`Feature flags, data migrations, and developer utilities.`,
        icon: Wrench,
        link: '/developer-tools',
        permission: 'canAccessDeveloperTools',
        hasSubSidebar: true,
      },
    ];

    return items.filter((item) => {
      if (item.permission != null && !perms.includes(item.permission)) {
        return false;
      }
      return true;
    });
  }, [permissions]);
}
